import { Flex, Text } from '@chakra-ui/react';
import { faBarsFilter, faFileLines, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  ButtonText,
  Modal,
  Table,
  useToast
} from '@morressier/alembic';
import {
  ISavedFilters,
  ISavedFilterSets,
  ITags
} from '@publishing-engine/domain/models';
import { removeSaveFilters } from 'src/api/mutations';
import ListLoader from 'src/components/ListLoader';
import ListView from 'src/layouts/ListView';
import SaveFilterText from './SaveFilterText';
import SubmissionLoader from './SubmissionLoader';
import { IDashboardSubmissionConfigResponse } from '@publishing-engine/domain/types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  savedFilters: ISavedFilters[] | undefined;
  workspaceId: string;
  availableFilters:
    | IDashboardSubmissionConfigResponse["filters"]
    | null
    | undefined;
  isSavedFiltersLoading: boolean;
  clearFilters: () => void;
  manageTags: ITags[];
  /**
   * Field catalogue used to label advanced-search criteria in each row's
   * summary. Optional so other callers keep working unchanged.
   */
  advancedSearchFields?: IDashboardSubmissionConfigResponse['advancedSearchFields'];
}
const ManageFiltersModal: React.FC<Props> = ({
  isOpen,
  onClose,
  workspaceId,
  savedFilters,
  availableFilters,
  isSavedFiltersLoading,
  clearFilters,
  manageTags,
  advancedSearchFields
}) => {
  const DeleteSaveFilterMutation = removeSaveFilters(workspaceId);
  const toast = useToast();
  const onDelete = async (filterId: string, name: string) => {
    const res = await DeleteSaveFilterMutation.mutateAsync(filterId);
    if (res.isSuccess) {
      return toast({
        variant: 'success',
        title: `${name} Filter deleted`
      });
    }
    return toast({
      variant: 'error',
      title: res.message
    });
  };
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="700px"
      closeButtonProps={{
        hidden: true
      }}
    >
      <ListView>
        <ListView.Title>
          <Flex alignItems="start">
            <FontAwesomeIcon icon={faBarsFilter} fontSize="42px" />
          </Flex>
          <Flex flexDirection="column" alignItems="start" marginTop={4} gap={2}>
            <Text textStyle="headingGroup">Manage Filters</Text>
            <Text textStyle="textContext">
              by deleting the ones you no longer need.
            </Text>
          </Flex>
        </ListView.Title>
        <ListView.Body>
          <Table>
            <Table.HeaderRow>
              <Table.HeaderCell flex="7">Name</Table.HeaderCell>
              <Table.HeaderCell flex="1" />
            </Table.HeaderRow>
            <Table.Body>
              {savedFilters?.length ? (
                savedFilters.map(filters => (
                  <Table.Row key={filters.id} spacing="md" gap={4}>
                    <Table.RowCell flex="7" textStyle="headingLine" isTruncated>
                      <Text textStyle="headingLine" isTruncated>
                        {filters.name}
                      </Text>
                      <SaveFilterText
                        saveFilters={filters.filters as ISavedFilterSets}
                        availableFilters={availableFilters}
                        manageTags={manageTags}
                        advancedSearchFields={advancedSearchFields}
                      />
                    </Table.RowCell>
                    <Table.RowCell flex="1" textStyle="headingLine">
                      <ButtonText
                        size="regular"
                        variant="caution"
                        iconLeft={<FontAwesomeIcon icon={faTrash} />}
                        onClick={() => {
                          onDelete(filters?.id ?? '', filters.name);
                          clearFilters();
                        }}
                      >
                        Delete
                      </ButtonText>
                    </Table.RowCell>
                  </Table.Row>
                ))
              ) : isSavedFiltersLoading ? (
                <ListLoader />
              ) : (
                    <SubmissionLoader
                      icon={faFileLines}
                      title="No Saved Filters"
                      subTitle="You have not saved any filters yet. Apply filters and click “Save Filter” to reuse them later."
                      bgColor="neutralSecondaryFill"
                    />
              )}
            </Table.Body>
          </Table>
          <Flex
            justifyContent="flex-end"
            gap={2}
            alignItems="center"
            marginTop={4}
            width="100%"
          >
            <Button onClick={onClose} variant="primary">
              Close
            </Button>
          </Flex>
        </ListView.Body>
      </ListView>
    </Modal>
  );
};

export default ManageFiltersModal;
